import type { Company, User } from 'resources/types'

/** A typed way to pull the role→requiredFields map from your Company */
export type ProfileRequirementsResult<TField extends string> = {
	isComplete: boolean;
	requiredFields: readonly TField[];
	missingFields: readonly TField[];
};

const isEmpty = (v: unknown): v is undefined | null | '' | [] =>
	v == null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0)

/**
 * Core checker – fully typed to your real User/Role/Company.
 * Provide a selector that returns the company’s role→requiredFields map.
 */
export function computeProfileRequirements(
	user: Pick<User, 'role'> & Partial<User>,
	company: Company,
): ProfileRequirementsResult<keyof User> {
	const map = company.requiredFields ?? {}
	const required = (map[user.role] ?? []) as readonly (keyof User & string)[]
	const missing = required.filter((k) => isEmpty(user[k]))
	return {
		isComplete: missing.length === 0,
		requiredFields: required,
		missingFields: missing,
	}
}

/** Reduce missing fields → validations map for your `useForm` */
export function buildRequiredValidations<TShape extends Record<string, unknown>, V>(
	missingFields: readonly (keyof TShape)[],
	requiredRule: V,
): Partial<Record<keyof TShape, V>> {
	const out = {} as Partial<Record<keyof TShape, V>>
	for (const k of missingFields) out[k] = requiredRule
	return out
}

/** Login gate helper */
export function shouldRedirectToCompleteProfile(
	user: Pick<User, 'role'> & Partial<User>,
	company: Company,
) {
	const { isComplete, missingFields } = computeProfileRequirements(user, company)
	return { shouldRedirect: !isComplete, missingFields }
}
